import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Handshake,
  Gauge,
  Cloud,
  ClipboardList,
  CalendarCheck,
  Wallet,
  Send,
  BarChart3,
  Settings
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import Navigation from './Navigation';
import Footer from './Footer';

// Import images
import heroImage from '../assets/about/hero-sewing-machine.jpg';
import missionImage from '../assets/about/mission-woman-tablet.jpg';
import founderAmandeep from '../assets/about/founder-amandeep.jpg';
import founderGurpreet from '../assets/about/founder-gurpreet.jpg';
import founderSimranjeet from '../assets/about/founder-simranjeet.jpg';

interface ProblemCard {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FeatureCard {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Founder {
  name: string;
  role: string;
  quote: string;
  image: string;
}

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Problem cards data
  const problems: ProblemCard[] = [
    {
      icon: <Handshake className="w-12 h-12 text-brand-blue" />,
      title: t('about.problem.1.title'),
      description: t('about.problem.1.description')
    },
    {
      icon: <Gauge className="w-12 h-12 text-brand-blue" />,
      title: t('about.problem.2.title'),
      description: t('about.problem.2.description')
    },
    {
      icon: <Cloud className="w-12 h-12 text-brand-blue" />,
      title: t('about.problem.3.title'),
      description: t('about.problem.3.description')
    }
  ];

  // Feature cards data
  const features: FeatureCard[] = [
    {
      icon: <ClipboardList className="w-12 h-12 text-brand-blue" />,
      title: t('about.features.1.title'),
      description: t('about.features.1.description')
    },
    {
      icon: <CalendarCheck className="w-12 h-12 text-brand-blue" />,
      title: t('about.features.2.title'),
      description: t('about.features.2.description')
    },
    {
      icon: <Wallet className="w-12 h-12 text-brand-blue" />,
      title: t('about.features.3.title'),
      description: t('about.features.3.description')
    },
    {
      icon: <Send className="w-12 h-12 text-brand-blue" />,
      title: t('about.features.4.title'),
      description: t('about.features.4.description')
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-brand-blue" />,
      title: t('about.features.5.title'),
      description: t('about.features.5.description')
    },
    {
      icon: <Settings className="w-12 h-12 text-brand-blue" />,
      title: t('about.features.6.title'),
      description: t('about.features.6.description')
    }
  ];

  // Founders data
  const founders: Founder[] = [
    {
      name: t('about.founders.1.name'),
      role: t('about.founders.1.role'),
      quote: t('about.founders.1.quote'),
      image: founderAmandeep
    },
    {
      name: t('about.founders.2.name'),
      role: t('about.founders.2.role'),
      quote: t('about.founders.2.quote'),
      image: founderGurpreet
    },
    {
      name: t('about.founders.3.name'),
      role: t('about.founders.3.role'),
      quote: t('about.founders.3.quote'),
      image: founderSimranjeet
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-[#fff8f0] py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h1 className="font-opensans text-[48px] leading-[68px] font-bold text-brand-blue">
                {t('about.hero.title')}
              </h1>
              <p className="font-manrope text-[18px] leading-[29px] text-[#19191f]">
                {t('about.hero.description')}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={heroImage}
                  alt={t('about.hero.alt')}
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-opensans text-[36px] leading-[40px] font-bold text-[#171a1f] mb-6">
              {t('about.problem.title')}
            </h2>
            <p className="font-opensans text-[20px] leading-[28px] text-[#565d6d] max-w-[896px] mx-auto">
              {t('about.problem.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_0px_1px_0px_rgba(23,26,31,0.07)] p-6 text-center"
              >
                <div className="flex justify-center mb-6">
                  {problem.icon}
                </div>
                <h3 className="font-opensans text-[24px] leading-[32px] font-semibold text-[#171a1f] mb-4 tracking-[-0.6px]">
                  {problem.title}
                </h3>
                <p className="font-manrope text-[16px] leading-[24px] text-[#565d6d]">
                  {problem.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 lg:py-28 bg-brand-blue">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={missionImage}
                  alt={t('about.mission.alt')}
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-opensans text-[36px] leading-[40px] font-bold text-white">
                {t('about.mission.title')}
              </h2>
              <div className="space-y-4 font-manrope text-[18px] leading-[29px] text-white">
                <p>
                  {t('about.mission.content.p1')}
                </p>
                <p>
                  {t('about.mission.content.p2')}
                </p>
                <p>
                  {t('about.mission.content.p3')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 lg:py-28 bg-[#fafafb]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-[896px] mx-auto"
          >
            <p className="font-opensans text-[48px] leading-[78px] font-light italic text-[#171a1f] mb-12">
              {t('about.quote.text')}
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <img
                src={founderGurpreet}
                alt={t('about.founders.2.name')}
                className="w-24 h-24 rounded-full object-cover flex-shrink-0"
                style={{ objectPosition: 'center top' }}
              />
              <div className="text-center md:text-left">
                <p className="font-opensans text-[20px] leading-[28px] text-[#565d6d]">
                  {t('about.quote.author')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-opensans text-[36px] leading-[40px] font-bold text-[#171a1f]">
              {t('about.features.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_0px_1px_0px_rgba(23,26,31,0.07)] p-6 text-center"
              >
                <div className="flex justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="font-opensans text-[24px] leading-[32px] font-semibold text-[#171a1f] mb-4 tracking-[-0.6px]">
                  {feature.title}
                </h3>
                <p className="font-manrope text-[16px] leading-[24px] text-[#565d6d]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-20 lg:py-28 bg-[#fff8f0]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-opensans text-[36px] leading-[40px] font-bold text-[#19191f]">
              {t('about.founders.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {founders.map((founder, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_0px_1px_0px_rgba(23,26,31,0.07)] p-8 text-center"
              >
                <div className="flex justify-center mb-6">
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="w-[77px] h-[77px] rounded-full object-cover"
                  />
                </div>
                <h3 className="font-opensans text-[24px] leading-[32px] font-semibold text-[#171a1f] mb-2 tracking-[-0.6px]">
                  {founder.name}
                </h3>
                <p className="font-manrope text-[18px] leading-[28px] font-medium text-brand-yellow mb-4">
                  {founder.role}
                </p>
                <p className="font-manrope text-[16px] leading-[24px] italic text-[#565d6d]">
                  "{founder.quote}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-brand-yellow">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-[768px] mx-auto"
          >
            <h2 className="font-opensans text-[36px] leading-[40px] font-bold text-[#19191f] mb-6">
              {t('about.cta.title')}
            </h2>
            <p className="font-opensans text-[20px] leading-[28px] text-[#19191f] mb-10">
              {t('about.cta.description')}
            </p>
            <button
              onClick={() => navigate('/auth?mode=signup')}
              className="bg-brand-blue text-white px-8 py-4 rounded-md font-manrope text-[18px] leading-[28px] font-medium hover:bg-brand-blue-700 transition-colors duration-200"
            >
              {t('about.cta.button')}
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
